import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatINR } from '../utils/api';

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    api.get('/vendors').then(d => { setVendors(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = vendors.filter(v =>
    !search || v.name.toLowerCase().includes(search.toLowerCase()) ||
    (v.service_type||'').toLowerCase().includes(search.toLowerCase())
  );

  const totalBudgeted    = vendors.reduce((s,v) => s+(v.total_budgeted||0), 0);
  const totalPaid        = vendors.reduce((s,v) => s+(v.total_paid||0), 0);
  const totalOutstanding = vendors.reduce((s,v) => s+(v.total_outstanding||0), 0);

  return (
    <div className="page-content">
      <div className="topbar">
        <div className="topbar-title">Vendors</div>
        <div className="topbar-right">
          <input style={{padding:'5px 10px',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',fontSize:12,width:180,fontFamily:'inherit'}}
            placeholder="Search vendors…" value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add Vendor</button>
        </div>
      </div>

      <div className="kpi-grid kpi-grid-4">
        <div className="kpi-card"><div className="kpi-label">Total Vendors</div><div className="kpi-value">{vendors.length}</div></div>
        <div className="kpi-card accent"><div className="kpi-label">Total Budgeted</div><div className="kpi-value red">{formatINR(totalBudgeted)}</div></div>
        <div className="kpi-card"><div className="kpi-label">Total Paid</div><div className="kpi-value green">{formatINR(totalPaid)}</div></div>
        <div className="kpi-card"><div className="kpi-label">Total Outstanding</div><div className="kpi-value danger">{formatINR(totalOutstanding)}</div></div>
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">All Vendors</span></div>
        {loading ? <div className="loading-state">Loading…</div> : filtered.length === 0 ? (
          <div className="empty-state"><div className="empty-state-icon">🏪</div>{vendors.length === 0 ? 'No vendors yet. Add one above.' : 'No results.'}</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Vendor</th><th>Service Type</th><th>Phone</th>
                <th className="num">Projects</th>
                <th className="num">Budgeted</th>
                <th className="num">Paid</th>
                <th className="num">Outstanding</th>
              </tr></thead>
              <tbody>
                {filtered.map(v => (
                  <tr key={v.id} onClick={() => navigate(`/vendors/${v.id}`)}>
                    <td>
                      <div style={{fontWeight:600,color:'var(--text-dark)'}}>{v.name}</div>
                      {v.email && <div style={{fontSize:10,color:'var(--text-light)'}}>{v.email}</div>}
                    </td>
                    <td style={{fontSize:11,color:'var(--text-light)'}}>{v.service_type || '—'}</td>
                    <td style={{fontSize:11,color:'var(--text-light)'}}>{v.phone || '—'}</td>
                    <td className="num">{v.project_count}</td>
                    <td className="num">{formatINR(v.total_budgeted)}</td>
                    <td className="num" style={{color:'var(--success)',fontWeight:600}}>{formatINR(v.total_paid)}</td>
                    <td className="num" style={{color:v.total_outstanding>0?'var(--danger)':'var(--success)',fontWeight:700}}>{formatINR(v.total_outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAdd && <VendorFormModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

export function VendorFormModal({ vendor, onClose, onSaved }) {
  const isEdit = !!vendor;
  const [form, setForm] = useState({
    name: vendor?.name||'', service_type: vendor?.service_type||'',
    gstin: vendor?.gstin||'', pan: vendor?.pan||'',
    phone: vendor?.phone||'', email: vendor?.email||'',
    bank_account: vendor?.bank_account||'', ifsc: vendor?.ifsc||'',
    bank_name: vendor?.bank_name||'', notes: vendor?.notes||'',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  function setF(k,v) { setForm(f=>({...f,[k]:v})); }

  async function handleSave() {
    if (!form.name) { setError('Vendor name is required.'); return; }
    setSaving(true); setError('');
    try {
      if (isEdit) await api.put(`/vendors/${vendor.id}`, form);
      else await api.post('/vendors', form);
      onSaved();
    } catch(err) { setError(err.message); setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:500}}>
        <div className="modal-header"><div className="modal-title">{isEdit?'Edit Vendor':'Add Vendor'}</div></div>
        <div className="modal-body">
          {error && <div className="setup-error">{error}</div>}
          <div className="form-row">
            <div className="form-group" style={{flex:2}}><label className="form-label">Vendor Name *</label>
              <input className="form-input" value={form.name} onChange={e=>setF('name',e.target.value)} autoFocus={!isEdit} /></div>
            <div className="form-group" style={{flex:1}}><label className="form-label">Service Type</label>
              <input className="form-input" value={form.service_type} onChange={e=>setF('service_type',e.target.value)} placeholder="e.g. Design" /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.phone} onChange={e=>setF('phone',e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Email</label><input className="form-input" value={form.email} onChange={e=>setF('email',e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">GSTIN</label><input className="form-input" value={form.gstin} onChange={e=>setF('gstin',e.target.value)} /></div>
            <div className="form-group"><label className="form-label">PAN</label><input className="form-input" value={form.pan} onChange={e=>setF('pan',e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Bank Account</label><input className="form-input" value={form.bank_account} onChange={e=>setF('bank_account',e.target.value)} /></div>
            <div className="form-group"><label className="form-label">IFSC</label><input className="form-input" value={form.ifsc} onChange={e=>setF('ifsc',e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Bank Name</label><input className="form-input" value={form.bank_name} onChange={e=>setF('bank_name',e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" value={form.notes} onChange={e=>setF('notes',e.target.value)} /></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?'Saving…':isEdit?'Save Changes':'Add Vendor'}</button>
        </div>
      </div>
    </div>
  );
}
